import { KeyTextField } from '@prismicio/client/*';
import {
  IShipment,
  IPromotionCoupon,
  IPromotionCode,
  IPaymentIntentError,
  PaymentMethodType,
  ShippingMethodTypes,
  MembershipPlan,
} from '.';

export type TrackCartEventType = 'LFAddToCart' | 'LFRemoveFromCart';
export type TrackReorderWidgetEventAction = 'open' | 'validate';
export type TrackSendReferralType = 'email' | 'link' | 'browser' | 'code';

export type EcommerceProductData = {
  sku: string;
  name: string;
  brand: string;
  category?: string;
  /**
   * Integer price (centimes)
   */
  price: number;
  quantity: number;
  url?: string;
  imageUrl?: string;
  compareAtPrice?: number;
};

export interface ISDKKlaviyo {
  account: (...args: any[]) => unknown;
  cacheEvent: (...args: any[]) => unknown;
  cookieDomain: (...args: any[]) => unknown;
  getGroupMembership: (...args: any[]) => unknown;
  identify: (...args: any[]) => unknown;
  isIdentified: (...args: any[]) => unknown;
  openForm: (...args: any[]) => unknown;
  push: (...args: any[]) => unknown;
  sendCachedEvents: (...args: any[]) => unknown;
  track: (...args: any[]) => unknown;
  trackViewedItem: (...args: any[]) => unknown;
}

export interface IProductKlaviyo {
  ProductName: string;
  ProductID: string | number;
  ProductCategories: Array<string>;
  ImageURL: string | undefined;
  ProductURL: string;
  Brand: string;
  ItemPrice: number;
  CompareAtPrice: number;
  SKU: string | number;
}

export type TrackCartEvent = {
  type: TrackCartEventType;
  data: {
    products: Array<EcommerceProductData>;
    context: AddToShoppingCartContext;
    currency: string;
    is_substitute: boolean;
    eventSource: 'app' | 'web';
  };
};

export type CartUpdatedEvent = {
  type: 'LFCartUpdated';
  data: {
    cart: Array<EcommerceProductData>;
    total_price: number;
    amount_until_free_delivery: number;
  };
};

export interface ITrackWishlistEvent {
  type: 'LFAddToWishlist';
  data: {
    products: Array<EcommerceProductData>;
    currency: string;
  };
}

export interface ITrackSendPrivateLabelSurvey {
  type: 'LFSendPrivateLabelSurvey';
  data: {
    surveyName: string;
  };
}

export interface ITrackViewItemEvent {
  type: 'LFViewItem';
  data: {
    products: Array<EcommerceProductData>;
    currency: string;
  };
}

export interface ITrackReviewProductEvent {
  type: 'LFReviewProduct';
  data?: {
    products: Array<Omit<EcommerceProductData, 'quantity'>>;
  };
}

export interface ITrackSubscribeToNewsletterEvent {
  type: 'LFSubscribeToNewsletter';
  data?: never;
}

export interface ITrackPaymentErrorEvent {
  type: 'LFCheckoutPaymentError';
  data: {
    error: IPaymentIntentError;
    paymentMethod: string | undefined;
  };
}

export interface ITrackMaapErrorEvent {
  type: 'LFMaapPaymentError';
  data: { failureType: 'subscription' | 'order' | 'subscription + order' };
}

export interface ITrackStartCheckoutEvent {
  type: 'LFStartCheckout';
  data: {
    currency: string;
    value: number;
    isMaap: boolean;
    products: Array<EcommerceProductData>;
  };
}

export interface ITrackAddShippingInfoEvent {
  type: 'LFAddShippingInfo';
  data: {
    currency: string;
    value: number;
    isMaap: boolean;
    shipping: IShipment;
    promotionCoupon: IPromotionCoupon | undefined;
    products: Array<EcommerceProductData>;
  };
}

export interface ITrackPurchaseEvent {
  type: 'LFPurchase';
  data: {
    currency: string;
    transactionId: string;
    value: number;
    isMaap: boolean;
    shipping: IShipment;
    shippingValue: number;
    promotionCoupon?: IPromotionCoupon | undefined;
    tax: number;
    paymentMethod: string;
    products: Array<EcommerceProductData>;
    orderCount: number;
  };
}

export interface ITrackReorderWidgetEvent {
  type: 'LFReorderWidget';
  data: {
    action: TrackReorderWidgetEventAction;
  };
}

export interface ITrackStartFreeTrialEvent {
  type: 'LFStartFreeTrial';
  data: {
    id: string | undefined;
    promotionCode: IPromotionCode | null | undefined;
    email: string | null | undefined;
    isMaap: boolean;
    freeTrialPaymentMethod: PaymentMethodType | 'unknown';
  };
}

export interface ITrackApplyFilterEvent {
  type: 'LFApplyFilter';
  data: {
    type: string;
    selected: string[];
  };
}

export interface ITrackPageViewEvent {
  type: 'LFPageView';
  data?: {
    personalizationCondition?: string | undefined;
    pageType?: string;
    originalLocation?: string | null;
    referralCampaignId?: string;
    isMaap?: boolean;
  };
}

export interface ITrackPWAInstalledEvent {
  type: 'LFPWAInstalled';
  data?: never;
}

export interface ITrackSendReferralEvent {
  type: 'LFSendReferral';
  data: {
    type: TrackSendReferralType | string;
    referralSharingType: TrackSendReferralType | string;
    sentCount: number;
    referralCampaignId: string;
  };
}

export interface ITrackRedeemReferralEvent {
  type: 'LFRedeemReferral';
  data?: never;
}

export interface ITrackSignUpEvent {
  type: 'LFSignUp';
  data: {
    method: string;
    isMaap: boolean;
  };
}

export interface ITrackOOSReplacementModalEvent {
  type: 'LFSubstitutionPopin';
  data?: {
    productSku: string;
    source: string;
    originalSource: string;
  };
}

export interface ITrackOOSReplacementEmptyEvent {
  type: 'LFSubstitutionNotFound';
  data?: {
    productSku: string;
    source: string;
  };
}

export interface ITrackSearchOpenEvent {
  type: 'LFSearchRecommendationOpen';
  data?: never;
}

export interface ITrackWarningModalEvent {
  type: 'LFWarningPopin';
  data?: never;
}

export interface ITrackLFGrowthbookExperimentViewed {
  type: 'LFGrowthbookExperimentViewed';
  data: {
    experimentVariation: number;
    experimentKey: string;
    experimentKeyVariation: string;
  };
}

export interface ITrackSearchRecommendationClickEvent {
  type: 'LFSearchRecommendationClick';
  data:
    | {
        recommendationType: 'collection';
        collectionName: string;
        collectionSlug: string;
        isSearchEmpty: boolean;
      }
    | {
        recommendationType: 'brand';
        brandName: string;
        brandSlug: string;
        isSearchEmpty: boolean;
      }
    | {
        recommendationType: 'product';
        itemName: string;
        itemBrand: string;
        itemCategory: string;
        /**
         * The Product's SKU
         */
        itemId: string;
        isSearchEmpty: boolean;
      }
    | {
        recommendationType: 'merch_card';
        isSearchEmpty: boolean;
      };
}
export interface ITrackSearchInputTypeEvent {
  type: 'LFSearchInputType';
  data?: never;
}

export interface ITrackSearchToCollectionClickEvent {
  type: 'LFSearchToCollectionClick';
  data: {
    searchTerm: string;
    collectionName: string;
    collectionSlug: string;
  };
}

export interface ITrackMinimumOrderAmountNotReached {
  type: 'LFMinimumOrderAmountNotReached';
  data: {
    cartValue: number;
  };
}

export interface ITrackOpenMembershipModalEvent {
  type: 'LFOpenMembershipModal';
  data: {
    origin: 'horizontal_product_card';
  };
}

export interface ITrackSignUpStartPasswordTypingEvent {
  type: 'LFStartPasswordTypeSignUp';
  data?: never;
}

export interface ITrackSignUpSubmitEvent {
  type: 'LFSubmitSignUp';
  data: {
    password_compliant: boolean;
    min_characters_criteria: boolean;
    special_characters_criteria: boolean;
    lowercase_criteria: boolean;
    capital_criteria: boolean;
    digit_criteria: boolean;
  };
}

export interface ITrackPasswordResetRequestedEvent {
  type: 'LFRequestResetPassword';
  data?: never;
}

export interface ITrackPasswordResetEvent {
  type: 'LFResetPassword';
  data?: never;
}

export interface ITrackPaymentMethodUpdatedEvent {
  type: 'LFPaymentMethodUpdated';
  data?: {
    payment_method_action: 'added' | 'deleted' | 'assigned as primary';
    update_source: string;
  };
}
export interface ITrackStartShippingAddressEvent {
  type: 'LFStartShippingAddress';
  data?: never;
}

export interface ITrackSubmitShippingAddressEvent {
  type: 'LFSubmitShippingAddress';
  data?: never;
}

export type TrackProductAnchorBannerButtonUsedType =
  | 'about'
  | 'co-creation'
  | 'tips'
  | 'vendor'
  | 'reviews'
  | 'main'
  | 'composition'
  | 'unknown';

export interface ITrackProductAnchorBannerButtonUsedEvent {
  type: 'LFProductAnchorBannerButtonUsed';
  data?: {
    productAnchorBannerButton: TrackProductAnchorBannerButtonUsedType;
  };
}

export interface ITrackCheckoutRoundUpEvent {
  type: 'LFCheckoutRoundUp';
  data?: {
    action: 'added' | 'removed';
    amount: number;
    currency: string;
  };
}

export interface IStartFirstOrderFormEvent {
  type: 'LFStartForm';
  data?: {
    formName: string;
  };
}

export interface IEndFirstOrderFormEvent {
  type: 'LFEndForm';
  data?: {
    formName: string;
  };
}
export interface ISubmitFirstOrderFormStepEvent {
  type: 'LFSubmitFormStep';
  data?: {
    [key: string]: any;
  };
}
export interface ITrackSubscriptionPlanChangeEvent {
  type: 'LFSubscriptionPlanChange';
  data?: {
    old_plan: MembershipPlan;
    new_plan: MembershipPlan;
    subscription_id: string;
    subscription_email: string;
    coupon_id?: string;
    coupon_name?: string;
  };
}

export interface ITrackStartMonthlySubscriptionEvent {
  type: 'LFStartMonthlySubscription';
  data?: {
    subscription_id?: string;
    subscription_email?: string;
    coupon_id?: string;
    coupon_name?: string;
  };
}

export interface IProductClickedOrderEvent {
  type: 'LFProductClickedOrder';
  data?: never;
}

export interface IDisplayDeliveryBarEvent {
  type: 'LFDisplayDeliveryBar';
  data?: {
    screen: 'address_bar' | 'shipping_method';
  };
}

export interface IPickDeliveryOptionEvent {
  type: 'LFPickDeliveryOption';
  data?: {
    shipping_method: ShippingMethodTypes;
  };
}

export interface ILFZwSlotsOpenedEvent {
  type: 'LFZwSlotsOpened';
  data?: never;
}

export interface ITrackSelectItemEvent {
  type: 'LFSelectItem';
  data: {
    item_list_id: string;
    item_list_impression_id: string;
    selected_item: {
      item_id: string;
      item_name: string;
      index: number;
    };
  };
}

export interface ITrackViewItemListEvent {
  type: 'LFViewItemList';
  data: {
    currency: string;
    item_list_id: string;
    item_list_impression_id: string;
    items: {
      item_id: string;
      item_name: string;
      index: number;
    }[];
  };
}

export interface ITrackManualAddressFormOpenedEvent {
  type: 'LFManualAddressFormOpened';
  data: {
    manual_address_form_cta_location: 'delivery_bar' | 'first_order_form';
    autocomplete_value: string;
  };
}

export interface ITrackManualAddressFormCompletedEvent {
  type: 'LFManualAddressFormCompleted';
  data: {
    manual_address_form_cta_location: 'delivery_bar' | 'first_order_form';
  };
}

export interface ITrackDisplayDeliveryOptionEvent {
  type: 'LFDisplayDeliveryOption';
  data: {
    courier_first_available_slot: number | null;
    zipcode: string;
  };
}

export type EventType =
  | TrackCartEvent
  | CartUpdatedEvent
  | ITrackWishlistEvent
  | ITrackSendPrivateLabelSurvey
  | ITrackViewItemEvent
  | ITrackReviewProductEvent
  | ITrackSubscribeToNewsletterEvent
  | ITrackPaymentErrorEvent
  | ITrackMaapErrorEvent
  | ITrackStartCheckoutEvent
  | ITrackAddShippingInfoEvent
  | ITrackPurchaseEvent
  | ITrackReorderWidgetEvent
  | ITrackStartFreeTrialEvent
  | ITrackApplyFilterEvent
  | ITrackPageViewEvent
  | ITrackPWAInstalledEvent
  | ITrackSendReferralEvent
  | ITrackRedeemReferralEvent
  | ITrackSignUpEvent
  | ITrackOOSReplacementModalEvent
  | ITrackOOSReplacementEmptyEvent
  | ITrackSearchOpenEvent
  | ITrackSearchRecommendationClickEvent
  | ITrackMinimumOrderAmountNotReached
  | ITrackLFGrowthbookExperimentViewed
  | ITrackSearchToCollectionClickEvent
  | ITrackOpenMembershipModalEvent
  | ITrackWarningModalEvent
  | ITrackSearchInputTypeEvent
  | ITrackSignUpStartPasswordTypingEvent
  | ITrackSignUpSubmitEvent
  | ITrackPasswordResetRequestedEvent
  | ITrackPasswordResetEvent
  | ITrackPaymentMethodUpdatedEvent
  | ITrackProductAnchorBannerButtonUsedEvent
  | ITrackStartShippingAddressEvent
  | ITrackSubmitShippingAddressEvent
  | ITrackCheckoutRoundUpEvent
  | IStartFirstOrderFormEvent
  | IEndFirstOrderFormEvent
  | ISubmitFirstOrderFormStepEvent
  | IProductClickedOrderEvent
  | IDisplayDeliveryBarEvent
  | IPickDeliveryOptionEvent
  | ILFZwSlotsOpenedEvent
  | ITrackSubscriptionPlanChangeEvent
  | ITrackStartMonthlySubscriptionEvent
  | ITrackSelectItemEvent
  | ITrackViewItemListEvent
  | ITrackManualAddressFormOpenedEvent
  | ITrackManualAddressFormCompletedEvent
  | ITrackDisplayDeliveryOptionEvent;

export type CartEventType = 'addToCart' | 'removeFromCart';

type OptionalShoppingCartFields = Partial<{
  widgetPosition: string;
  widgetName: string | KeyTextField;
  previous_order_index: number;
}>;

export type AddToShoppingCartContext = {
  source: AddToShoppingCartSource;
} & OptionalShoppingCartFields;

export enum AddToShoppingCartSource {
  BUNDLE = 'Bundle',
  CAROUSEL = 'Carousel',
  DEFAULT = 'Default',
  RECOMMENDATION = 'Recommendation',
  SIDE_CART = 'SideCart',
  SEARCH_INPUT = 'SearchInput',
  SEARCH_RECOMMENDATION = 'SearchRecoOpen',
  CHECKOUT_SUGGESTION = 'CheckoutSuggestion',
  PREVIOUS_ORDER = 'previousOrder',
}
